import { colones } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Bike, ChevronDown, ChevronUp, Download, Sparkles, Star, AlertTriangle, Wrench } from "lucide-react";

const ORIGEN_BADGE = {
  maestra: { txt: "Maestra", cls: "bg-amber-100 text-amber-800 border border-amber-300" },
  generada: { txt: "Generada IA", cls: "bg-indigo-100 text-indigo-700 border border-indigo-300" },
  manual: { txt: "Manual", cls: "bg-slate-100 text-slate-600 border border-slate-300" },
};

export const PlantillaCard = ({ p, isAdmin, expandida, onToggle, onEditar, onEliminar, onCargar, onGenerarFaltantes, mostrarGenerarFaltantes, puedeGenerarIA, onRepararHuerfana, onAsignarServicio }) => {
  const moPropia = Number(p.mano_de_obra) || 0;
  const frtPropio = Number(p.frt) || 0;
  const moEfectiva = p.mano_de_obra_efectiva != null ? Number(p.mano_de_obra_efectiva)
    : (p.mano_obra_efectiva ? p.mano_obra_efectiva.reduce((a, m) => a + (Number(m.monto) || 0), 0) : moPropia);
  const frtEfectivo = Math.round((p.frt_efectivo != null ? Number(p.frt_efectivo)
    : (p.mano_obra_efectiva || []).reduce((a, m) => a + (Number(m.frt) || 0), 0)) * 10) / 10;
  const hereda = p.hereda_mano_obra || moEfectiva !== moPropia || frtEfectivo !== frtPropio;
  // El servicio se asocia a maestras/manuales. Marcamos las que aún no tienen ninguno.
  const asociable = p.origen === "maestra" || p.origen === "manual";
  const sinServicio = asociable && p.tiene_servicio === false;
  return (
  <div className="bg-white border border-border rounded-sm" data-testid={`plantilla-card-${p.id}`}>
    <div className="p-3 flex items-start justify-between gap-2 cursor-pointer" onClick={onToggle}>
      <div className="min-w-0">
        <h3 className="font-heading font-bold text-sm flex items-center gap-1 min-w-0">
          {p.es_maestra && <Star size={12} className="text-amber-500 fill-amber-400 shrink-0" data-testid={`plantilla-maestra-badge-${p.id}`} />}
          <span className="truncate">{p.nombre}</span>
        </h3>
        <div className="flex flex-wrap gap-1 mt-1">
          {p.origen && ORIGEN_BADGE[p.origen] && (
            <span data-testid={`plantilla-origen-${p.id}`}
              className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${ORIGEN_BADGE[p.origen].cls}`}>
              {ORIGEN_BADGE[p.origen].txt}
            </span>
          )}
          {p.categoria && <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded-sm font-medium">{p.categoria.nombre}</span>}
          {p.huerfana && (
            <span data-testid={`plantilla-huerfana-badge-${p.id}`}
              className="text-[10px] px-1.5 py-0.5 rounded-sm font-semibold bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-0.5">
              <AlertTriangle size={9} /> Huérfana
            </span>
          )}
          {sinServicio && (
            <span data-testid={`plantilla-sin-servicio-badge-${p.id}`}
              className="text-[10px] px-1.5 py-0.5 rounded-sm font-semibold bg-rose-100 text-rose-700 border border-rose-300 flex items-center gap-0.5">
              <AlertTriangle size={9} /> Sin servicio
            </span>
          )}
          {p.modelos?.map((m) => (
            <span key={m.id} className="text-[10px] bg-accent text-accent-foreground px-1.5 py-0.5 rounded-sm font-medium flex items-center gap-0.5">
              <Bike size={9} />{m.nombre_mostrar || `${m.marca} ${m.modelo}`}
            </span>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {p.items_efectivos
            ? `${p.items_efectivos.length} repuestos${p.items_efectivos.length !== (p.items?.length ?? 0) ? ` (${p.items?.length ?? 0} propios + heredados)` : ""}`
            : `${p.num_repuestos ?? p.items?.length ?? 0} repuestos${p.plantillas_padre_ids?.length ? " + heredados" : ""}`}
          {" · "}Mano de obra: <span className="font-semibold text-foreground">{colones(moEfectiva)}</span>
          {hereda && moEfectiva !== moPropia && <span className="text-[10px] text-teal-700"> (con herencia · propio {colones(moPropia)})</span>}
          {frtEfectivo > 0 && <> · FRT: <span className="font-semibold text-foreground">{frtEfectivo}</span>
            {hereda && frtEfectivo !== frtPropio && <span className="text-[10px] text-teal-700"> (propio {frtPropio})</span>}</>}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {(puedeGenerarIA ?? mostrarGenerarFaltantes) && (
          <Button data-testid={`generar-faltantes-${p.id}`} variant="ghost" size="icon"
            className="h-7 w-7 text-indigo-600 hover:bg-indigo-50" title="Generar plantillas con IA"
            onClick={(e) => { e.stopPropagation(); onGenerarFaltantes?.(); }}>
            <Sparkles size={14} />
          </Button>
        )}
        {isAdmin && (<>
          <Button data-testid={`editar-plantilla-${p.id}`} variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onEditar(); }}><Pencil size={13} /></Button>
          <Button data-testid={`eliminar-plantilla-${p.id}`} variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); onEliminar(); }}><Trash2 size={13} /></Button>
        </>)}
        {expandida ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </div>
    </div>
    {expandida && (
      <div className="border-t border-border p-3 space-y-2">
        {p.descripcion && <p className="text-xs text-muted-foreground">{p.descripcion}</p>}
        {p.palabras_clave && <p className="text-xs text-muted-foreground"><span className="font-semibold">Palabras clave:</span> {p.palabras_clave}</p>}
        {(p.plantillas_padre?.length > 0) && (
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold">Hereda de:</span>{" "}
            {p.plantillas_padre.map((pp) => pp.nombre).join(", ")}
          </p>
        )}
        {!p.items_efectivos && <p className="text-xs text-muted-foreground py-1">Cargando detalle…</p>}
        <table className="w-full text-xs">
          <thead><tr className="text-left text-muted-foreground border-b border-border">
            <th className="py-1 pr-2">Código</th><th className="py-1 pr-2">Repuesto</th><th className="py-1 text-right">Cant.</th></tr></thead>
          <tbody>
            {(p.items_efectivos || []).map((it, idx) => (
              <tr key={it.repuesto_id || it.codigo_parte || idx} className="border-b border-border/50">
                <td className="py-1 pr-2 font-mono font-semibold">{it.codigo_parte}</td>
                <td className="py-1 pr-2">{it.nombre}
                  {it.opcional && <span className="ml-1.5 text-[9px] bg-zinc-200 text-zinc-700 px-1 py-0.5 rounded-sm uppercase font-semibold">Opcional</span>}
                  {it.heredado_de && <span className="ml-1.5 text-[9px] bg-amber-100 text-amber-800 px-1 py-0.5 rounded-sm uppercase font-semibold" title={`Heredado de: ${it.heredado_de}`}>Heredado</span>}
                </td>
                <td className="py-1 text-right">{it.cantidad}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Button data-testid={`cargar-plantilla-${p.id}`} size="sm" className="w-full" onClick={onCargar}>
          <Download size={14} className="mr-1" /> Cargar en una cotización
        </Button>
        {isAdmin && sinServicio && onAsignarServicio && (
          <Button data-testid={`asignar-servicio-btn-${p.id}`} size="sm" variant="outline"
            className="w-full border-rose-300 text-rose-700 hover:bg-rose-50" onClick={onAsignarServicio}>
            <Wrench size={14} className="mr-1" /> Asignar o crear servicio
          </Button>
        )}
        {isAdmin && p.huerfana && onRepararHuerfana && (
          <Button data-testid={`reparar-huerfana-${p.id}`} size="sm" variant="outline"
            className="w-full border-amber-400 text-amber-700 hover:bg-amber-50" onClick={onRepararHuerfana}>
            <Wrench size={14} className="mr-1" /> Reparar (reasignar / desvincular)
          </Button>
        )}
        {isAdmin && p.es_maestra && (puedeGenerarIA ?? mostrarGenerarFaltantes) && (
          <Button data-testid={`generar-ia-plantilla-${p.id}`} size="sm" variant="outline"
            className="w-full border-indigo-400 text-indigo-600 hover:bg-indigo-50" onClick={onGenerarFaltantes}>
            <Sparkles size={14} className="mr-1" /> Generar plantillas con IA
          </Button>
        )}
      </div>
    )}
  </div>
  );
};
